// The reach-field control. The console feeds it context (point, vessel cruise
// speed, wind & wave limits) via update(); the control owns the mesh fetch, the
// limit-anchored colouring, and which step is painted. One mesh carries both
// variables, so switching Wind↔Waves is instant (no refetch).
//
// Its UI is split deliberately, and neither half is arbitrary:
//   - the Wind / Waves toggles render into the console's ONE layer list (see
//     WindControlOptions.toggleMount), because they are layer choices;
//   - what stays floating on the map is the field's own valid time and the
//     limit legend, because both are read against the field itself.
// It no longer carries a transport. The stack's transport drives the shared
// cursor and this control follows it — the field is a pure subscriber.

import type { Map as MlMap } from "maplibre-gl";
import {
  indexAtInstant,
  parseValidTime,
  type FieldMesh,
} from "../forecast/fieldmesh.js";
import { getCursor, subscribeCursor } from "../time/cursor.js";
import {
  setField,
  showFieldHour,
  recolorField,
  clearField,
  isLeadFaded,
  CONFIDENT_H,
  type FieldLayer,
  type FieldLimits,
} from "./windfield.js";
import { setArrows, clearArrows, meshHasDir } from "./arrowfield.js";
import { fmtLimitLen, lenUnit } from "../ui/units.js";
import type { FieldKind } from "../forecast/fieldmesh.js";

/** A layer discriminant that reached here unhandled is a bug, not a default. */
function assertNever(x: never): never {
  throw new Error(`unhandled field layer: ${String(x)}`);
}

export interface WindCtx {
  /**
   * The mesh to paint — the viewport window, owned and fetched by the console.
   *
   * This control used to fetch its own grid, keyed on a per-point extent, which
   * needed a debounce, a request-id race guard and a cache key just to survive
   * the extent changing on every click. The console owning the one mesh removed
   * the reason for all of it.
   */
  mesh: FieldMesh | null;
  /** Colour-ramp anchors. Null falls back to a stated default — the field is
   *  about WHERE the weather is, so it should still draw before a vessel is
   *  picked; only the meaning of "red" waits on the limits. */
  windLimitKn: number | null;
  waveLimitM: number | null;
}

export interface WindControlOptions {
  /**
   * Where to put the Wind / Waves checkboxes. Given a container, they render
   * there instead of on the map.
   *
   * The console hands this the layer list in the Observations workspace, so the
   * app has ONE layer surface. Two competing surfaces — a floating box with its
   * own Wind/Waves boxes plus a separate Overlays control — meant a user looking
   * for "why can't I see waves" had two places to look and no way to know which.
   * The scrubber and legend stay on the map: those are time and colour key, and
   * they are read against the field itself.
   */
  toggleMount?: HTMLElement;
  /** Fired when the active layer changes (including to null = field off). The
   *  console uses it to keep the reach veil in step — the veil dims the field,
   *  so a bare basemap must not stay dimmed by a veil for a layer that is gone. */
  onLayerChange?: (layer: FieldLayer | null) => void;
}

export interface WindControl {
  update(map: MlMap | null, ctx: WindCtx): void;
  /** The Wind / Waves toggle row, so a host can place it in its own layer list. */
  toggles: HTMLElement;
  /** Which layer is showing (wind, wave, the combined Limits view), or null when
   *  the field is off. The console reads this to keep layer-dependent chrome (the
   *  reach veil) in step with the map. */
  kind(): FieldLayer | null;
  /** Drop the shared-cursor subscription. The console keeps one control for its
   *  lifetime, so this exists for correctness rather than for routine use. */
  destroy(): void;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WIND_FALLBACK = 25;
const WAVE_FALLBACK = 2;

// Deliberately NOT routed through the ui/time.ts clock toggle: this readout is
// the timeline's fixed UTC cross-check (see the convention note in
// time/cursor.ts) — model steps are named in UTC, and one surface keeping that
// clock whatever the console is set to is what lets an operator reconcile the
// field against a model run or a met office bulletin.
function fmtUtc(iso: string): string {
  const t = parseValidTime(iso);
  if (Number.isNaN(t)) return iso;
  const d = new Date(t);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} · ${hh}:00 UTC`;
}

/** The step nearest the present moment. Delegates to a search over the mesh's own
 *  valid times: steps are hourly only to f120 and 3-hourly beyond, so deriving the
 *  index arithmetically drifts by up to ~48 h across a 7-day grid. */
function nowIndex(mesh: FieldMesh): number {
  return indexAtInstant(mesh, Date.now());
}

export function createWindControl(
  container: HTMLElement,
  opts: WindControlOptions = {},
): WindControl {
  let map: MlMap | null = null;
  let ctx: WindCtx = { mesh: null, windLimitKn: null, waveLimitM: null };
  // The combined Limits layer is on from the start — it is the product-level view
  // this console opens on, painting how close the forecast is to WHICHEVER limit is
  // nearer to breaking. It supersedes commit 54c5a17, which made Wind default-on:
  // Wind alone answers half the go/no-go question (a calm-wind day can still be a
  // no-go on swell), so opening on it under-warned. Opening with the map bare made
  // the whole thing look broken until you found a checkbox — the locator inset
  // painted a field the map did not, which reads as a bug rather than as a setting.
  // The Limits layer has stated fallbacks (WIND_FALLBACK / WAVE_FALLBACK), so it
  // draws before a vessel is picked. Null remains reachable: unticking all three is
  // how you get the basemap back.
  let kind: FieldLayer | null = "limits";
  let mesh: FieldMesh | null = null;
  let hour = 0;
  let lastKind: FieldLayer | null = null;
  let lastWind: number | null = null;
  let lastWave: number | null = null;

  // --- DOM -----------------------------------------------------------------
  const box = document.createElement("div");
  box.className = "windctl";

  const toggles = document.createElement("div");
  toggles.className = "windctl-toggles";
  const cbLimits = document.createElement("input");
  cbLimits.type = "checkbox";
  cbLimits.checked = true; // must agree with `kind` above, or the box lies about the map
  const cbWind = document.createElement("input");
  cbWind.type = "checkbox";
  const cbWave = document.createElement("input");
  cbWave.type = "checkbox";
  const lLimits = document.createElement("label");
  lLimits.className = "windctl-toggle";
  // "Workability", not "Limits" (Les, 2026-07-28): the layer answers "can I
  // work here", and "Limits" already names the vessels' operating-limits table.
  lLimits.append(cbLimits, document.createTextNode("Workability"));
  const lWind = document.createElement("label");
  lWind.className = "windctl-toggle";
  lWind.append(cbWind, document.createTextNode("Wind"));
  const lWave = document.createElement("label");
  lWave.className = "windctl-toggle";
  lWave.append(cbWave, document.createTextNode("Waves"));
  // Direction arrows — a MODIFIER of the active layer, not a fourth layer, so it
  // sits outside selectLayer's exclusivity. Default on (the directional read is
  // the point of the feature); hidden entirely while the mesh carries no
  // direction planes, because a checkbox that can do nothing is a UI stub.
  const cbArrows = document.createElement("input");
  cbArrows.type = "checkbox";
  cbArrows.checked = true;
  const lArrows = document.createElement("label");
  lArrows.className = "windctl-toggle";
  // The text is REWRITTEN per layer (see syncArrows) and never left as a generic
  // "Direction arrows". On Wind and Waves the arrows are unambiguous because the
  // layer names itself and there is nothing to choose. On WORKABILITY the raster
  // is wind and wave blended, so the arrows must say which of the two they are,
  // and the radios below let the operator say which they want.
  const arrowsText = document.createTextNode("Direction arrows");
  lArrows.append(cbArrows, arrowsText);
  lArrows.style.display = "none"; // shown when a mesh with directions arrives

  // WHICH field the arrows draw, on the one layer where the map itself does not
  // answer it. Radios rather than a hidden preference or a cycling button: both
  // options are visible at once, the chosen one is visible without clicking, and
  // it is the same label-plus-input row every other entry in this control is.
  //
  // Wave is the default. The Workability ramp is the nearer-limit of wind and
  // wave, and on TDI-Brooks' work — coring, box-core, ROV over the side — it is
  // the sea that pins it far more often than the wind; the sea's set is also the
  // heading a master holds, and the one thing the wind layer next door cannot
  // show you. It is one click either way, and the map says which is drawn.
  let arrowField: FieldKind = "wave";
  const pick = document.createElement("span");
  pick.className = "windctl-arrowpick";
  const radios: Record<FieldKind, HTMLInputElement> = {} as Record<FieldKind, HTMLInputElement>;
  // Unique per control instance: two of these on one page (console + a harness)
  // sharing a radio NAME would fight over the selection.
  const groupName = `windctl-arrowfield-${Math.random().toString(36).slice(2, 8)}`;
  for (const k of ["wind", "wave"] as const) {
    const rb = document.createElement("input");
    rb.type = "radio";
    rb.name = groupName;
    rb.checked = k === arrowField;
    const lb = document.createElement("label");
    lb.className = "windctl-toggle";
    lb.append(rb, document.createTextNode(k === "wave" ? "Waves" : "Wind"));
    lb.title =
      k === "wave"
        ? "Arrows show where the WAVES are running"
        : "Arrows show where the WIND is blowing";
    rb.addEventListener("change", () => {
      if (!rb.checked) return;
      arrowField = k;
      syncArrows();
    });
    radios[k] = rb;
    pick.appendChild(lb);
  }
  pick.style.display = "none"; // only on the layer that blends two fields
  const title = document.createElement("span");
  title.className = "windctl-title";
  // The map field is the current viewport window, not a reach disc around the site
  // (commit b10dfbd stopped clipping to a steaming radius). Calling it "1-day steam"
  // here described that dead behaviour and, sat next to a time scrubber, read as a
  // 24-hour forecast horizon — a claim this control has never made.
  title.textContent = "Forecast field";
  // Limits FIRST: it is the product-level view (nearer-limit of wind or wave), with
  // the two single-variable layers offered beneath it.
  toggles.append(title, lLimits, lWind, lWave, lArrows, pick);
  if (opts.toggleMount !== undefined) {
    toggles.classList.add("windctl-toggles-hosted");
    opts.toggleMount.appendChild(toggles);
  } else {
    box.appendChild(toggles);
  }

  // No transport here any more — the stack's transport bar drives the shared
  // cursor and this control follows it. What remains is the field's OWN valid
  // time, which is worth showing precisely because it can differ from the
  // playhead: past f120 the field is 3-hourly, so a playhead at 14:00 is being
  // answered by a field valid at 12:00, and only this line says so.
  // The layer's NAME, large, on the map itself. The colours are meaningless
  // without knowing which quantity they encode, and until now the only place
  // that said so was a checkbox in a side panel. Display-only on purpose: the
  // layer LIST stays the one control surface (see toggleMount above) — this is
  // the map stating what it is showing, not a second place to change it.
  const layerBig = document.createElement("div");
  layerBig.className = "windctl-layerbig";
  layerBig.title = "The map layer being painted — change it in the Layers list";
  box.appendChild(layerBig);

  // WHICH FIELD the arrows are. This line is load-bearing, not decoration: every
  // arrow on the map is now the same ordinary shafted arrow, so shape says
  // nothing about which variable a heading belongs to and only these words do.
  // It is stated on all three layers, not just the blended one — a label that
  // appears only where it is ambiguous teaches the reader to stop looking.
  const arrowline = document.createElement("div");
  arrowline.className = "windctl-arrows";
  box.appendChild(arrowline);

  const bar = document.createElement("div");
  bar.className = "windctl-bar";
  bar.style.display = "none";
  const readout = document.createElement("span");
  readout.className = "windctl-time";
  bar.append(readout);
  box.appendChild(bar);

  const legend = document.createElement("div");
  legend.className = "windctl-legend";
  legend.style.display = "none";
  const legLo = document.createElement("span");
  legLo.textContent = "0";
  const legRamp = document.createElement("span");
  legRamp.className = "windctl-ramp";
  const legHi = document.createElement("span");
  legHi.className = "windctl-leg-hi";
  legend.append(legLo, legRamp, legHi);
  box.appendChild(legend);

  // The field's provenance, stated on the map itself: model and cycle. Every
  // chart line names its model; the map — the largest data surface on screen —
  // said nothing about where its colours came from or how fresh they were. The
  // cycle doubles as the freshness stamp: "12z" that never advances is the
  // pipeline being down, visible to the person it matters to.
  const srcline = document.createElement("div");
  srcline.className = "windctl-src";
  box.appendChild(srcline);

  const status = document.createElement("div");
  status.className = "windctl-status";
  box.appendChild(status);
  container.appendChild(box);

  // With the toggles hosted in the layer list, this box holds only the scrubber,
  // the legend and a status line — all of which are empty until a field is on.
  // An empty bordered card floating over the map is chrome with nothing in it,
  // so hide it until it has something to say.
  const hosted = opts.toggleMount !== undefined;
  const syncBox = (): void => {
    if (!hosted) return;
    box.hidden =
      bar.style.display === "none" &&
      legend.style.display === "none" &&
      layerBig.textContent === "" &&
      arrowline.textContent === "" &&
      srcline.textContent === "" &&
      status.textContent === "";
  };

  // --- helpers -------------------------------------------------------------
  // Both limits, with the stated fallbacks so the field draws before a vessel is
  // picked. The renderer takes the pair for every layer; wind/wave ignore the half
  // they do not use, the Limits layer needs both.
  const limits = (): FieldLimits => ({
    windKn: ctx.windLimitKn ?? WIND_FALLBACK,
    waveM: ctx.waveLimitM ?? WAVE_FALLBACK,
  });

  // Whether the layer's arrow plane is the operator's to choose. Wind and Waves
  // answer it themselves — the layer IS the field, and offering a choice there
  // would let the map paint one variable and arrow another. Only the blended
  // Workability layer has a genuine question to ask.
  const arrowsAreAChoice = (): boolean =>
    kind === "limits" && mesh !== null && meshHasDir(mesh, "wind") && meshHasDir(mesh, "wave");

  // Which DIRECTION plane draws — exactly one, always. Waves show wave; Wind
  // shows wind; WORKABILITY shows whichever the operator picked, defaulting to
  // wave (see `arrowField`). Null = the mesh has no such plane (old server) or
  // no layer is up, and in both cases the arrows and their controls vanish.
  //
  // Both fields interleaved lived here twice and is not coming back. The glyph
  // COUNT was unchanged, which is what the idea was sold on, but count was never
  // the cost: with one field the eye reads a flow, with two it must CLASSIFY
  // every glyph before it can read anything, and at operational density it
  // cannot. Worse, it is what forced the sea to carry a shape of its own —
  // colour is spent on the ramp, so shape was the only channel left — and every
  // shape invented for that job collided with something a navigator already
  // knows (chevron: overfalls; two heads: ground track) or read as nothing at
  // all. One field at a time dissolves the whole problem.
  const dirKind = (): FieldKind | null => {
    if (mesh === null || kind === null) return null;
    if (kind === "wave") return meshHasDir(mesh, "wave") ? "wave" : null;
    if (kind === "wind") return meshHasDir(mesh, "wind") ? "wind" : null;
    // Workability: the operator's pick, degrading honestly to whichever plane a
    // mesh actually carries rather than to nothing.
    if (meshHasDir(mesh, arrowField)) return arrowField;
    return meshHasDir(mesh, "wind") ? "wind" : meshHasDir(mesh, "wave") ? "wave" : null;
  };
  const syncArrows = (): void => {
    const dk = dirKind();
    lArrows.style.display = dk === null ? "none" : "";
    // Whether this LAYER offers the choice, which is not the same as whether the
    // radios are on screen: the label follows the layer, so it does not flip its
    // own wording when the arrows are switched off and on. The radios follow the
    // checkbox, because a choice between two kinds of nothing is noise.
    const choose = dk !== null && arrowsAreAChoice();
    pick.style.display = choose && cbArrows.checked ? "" : "none";
    // Say which plane the arrows draw, every time, on every layer — in the
    // control AND on the map. With one glyph for every quantity, the words are
    // the whole distinction.
    if (dk !== null) {
      arrowsText.nodeValue = choose
        ? "Direction arrows"
        : dk === "wave"
          ? "Wave direction"
          : "Wind direction";
      lArrows.title = choose
        ? "Which field the arrows draw is yours to choose — the map names the one it is drawing"
        : dk === "wave"
          ? "Arrows show the direction the waves are running"
          : "Arrows show the direction the wind is going";
    }
    arrowline.textContent =
      dk === null || !cbArrows.checked
        ? ""
        : dk === "wave"
          ? "arrows: wave direction"
          : "arrows: wind direction";
    arrowline.title =
      dk === "wave"
        ? "Every arrow on the map is a wave heading, pointing where the sea is running"
        : "Every arrow on the map is a wind heading, pointing where the wind is going";
    syncBox();
    if (map === null) return;
    if (dk === null || mesh === null || !cbArrows.checked) {
      clearArrows(map);
      return;
    }
    // setArrows both installs and retargets — it rebuilds the decimated points
    // for the current mesh/plane/hour, so one call serves every path here.
    setArrows(map, mesh, dk, hour);
  };
  cbArrows.addEventListener("change", syncArrows);

  const setLegend = (): void => {
    switch (kind) {
      case null:
        layerBig.textContent = "";
        return;
      // Each case names the layer in the big map label as well as the legend —
      // the two travel together, because both answer "what am I looking at".
      case "limits":
        layerBig.textContent = "WORKABILITY";
        // The Limits layer cannot print one number with one unit — it is two
        // limits at once — so the legend prints the MEANING. Red is the ramp's
        // "at limit" band whichever variable is binding.
        legLo.textContent = "0";
        legHi.textContent = "at limit";
        legend.title = "red = at or over your wind or wave operating limit";
        return;
      case "wind":
      case "wave": {
        layerBig.textContent = kind === "wave" ? "WAVES" : "WIND";
        legLo.textContent = "0";
        // The wave limit prints through the same formatter as the chart's limit
        // line (ui/units.ts), so the map legend and the track can never disagree.
        legHi.textContent =
          kind === "wave"
            ? ctx.waveLimitM !== null
              ? `limit ${fmtLimitLen(ctx.waveLimitM)}`
              : `limit — ${lenUnit()}`
            : `limit ${ctx.windLimitKn !== null ? ctx.windLimitKn : "—"} kn`;
        legend.title = "";
        return;
      }
      default:
        assertNever(kind);
    }
  };
  /**
   * True when the playhead has run past the field grid's last step, so what is on
   * the map is that last step rather than the moment being pointed at.
   *
   * DERIVED, never stored. As a cached flag it had to be refreshed in every place
   * either side could change — the cursor moving, a mesh arriving, a teardown —
   * and missing one produced exactly the failure it exists to prevent: the map
   * painting Wednesday while the playhead read Friday, silently. A function of
   * (cursor, mesh) cannot be stale.
   */
  const isPastEnd = (): boolean => {
    const t = getCursor();
    const last = mesh === null ? undefined : mesh.times[mesh.times.length - 1];
    return t !== null && last !== undefined && Number.isFinite(last) && t > last + 1_800_000;
  };
  // The mirror of isPastEnd, found the same way its twin was: by an operator
  // noticing. The field now carries a rolling stored history, but the timeline
  // can still reach earlier than its first step, and scrubbing there clamps to
  // step 0 — so without this note the map silently shows start-of-history
  // weather under a playhead reading earlier still, which is the same silent
  // lie "field ends here" was built to kill, pointed backwards. Same half-hour
  // tolerance, same styling, same honesty.
  const isBeforeStart = (): boolean => {
    const t = getCursor();
    const first = mesh === null ? undefined : mesh.times[0];
    return t !== null && first !== undefined && Number.isFinite(first) && t < first - 1_800_000;
  };
  // An in-range step with NO data anywhere in the window: the stored history is
  // stitched from prior model runs, and a run the archive no longer holds
  // NaN-fills its hours rather than aborting the grid. Painting nothing there is
  // honest; painting nothing UNLABELLED reads as a rendering fault on a go/no-go
  // console, so the readout must say why the map went quiet. All-NaN only means
  // "unarchived hour" if some OTHER step has data — over land every step is
  // all-NaN and the note must stay silent. Verdicts are cached per mesh: the
  // scan is O(nx·ny) per step and the transport scrubs ~10 steps a second.
  let emptySteps: (boolean | undefined)[] = [];
  let meshHasData: boolean | undefined;
  const allNaN = (a: Float32Array | undefined): boolean => {
    if (a === undefined) return true;
    for (let i = 0; i < a.length; i++) if (Number.isFinite(a[i])) return false;
    return true;
  };
  const stepEmpty = (i: number): boolean => {
    if (mesh === null) return false;
    let v = emptySteps[i];
    if (v === undefined) {
      v = allNaN(mesh.wind[i]) && allNaN(mesh.wave[i]);
      emptySteps[i] = v;
    }
    return v;
  };
  const isDataGap = (): boolean => {
    if (mesh === null || !stepEmpty(hour)) return false;
    if (meshHasData === undefined) {
      meshHasData = mesh.times.some((_, i) => !stepEmpty(i));
    }
    return meshHasData;
  };
  const setReadout = (): void => {
    if (mesh === null) {
      readout.textContent = "";
      return;
    }
    // Two things the map must not leave unsaid.
    //
    // Muted colour: a field that quietens itself with no explanation reads as a
    // rendering fault rather than as a statement about lead time.
    //
    // Ran out: the grid is shorter than the charts (48 h against 7 days today), so
    // scrub far enough and the field clamps to its last step and keeps painting it.
    // The valid time alone does say so, but only to someone diffing two dates —
    // the map would otherwise show Tuesday's weather while the playhead reads
    // Friday, which is the worst failure this console could have.
    const faded = isLeadFaded(mesh, hour);
    const over = isPastEnd();
    const before = isBeforeStart();
    const gap = !before && !over && isDataGap();
    const note = before
      ? "  ·  field starts here — earlier hours are charts-only"
      : over
        ? "  ·  field ends here"
        : gap
          ? "  ·  no field data for this hour"
          : faded
            ? `  ·  beyond ${CONFIDENT_H} h`
            : "";
    readout.textContent = fmtUtc(mesh.timesUtc[hour] ?? "") + note;
    readout.classList.toggle("windctl-faded", faded || over || before || gap);
    readout.classList.toggle("windctl-stale", over || before || gap);
    // cycle "2026072012" → "run 20 Jul 12z". Malformed → just the model name;
    // never invent a date. The model name comes from the RESPONSE's source —
    // the server returns the PICKED REGION's provenance, and the field is no
    // longer one model everywhere: over the Black Sea it is CMEMS regional
    // waves + GFS wind, and a hardcoded "NOAA GFS-Wave" there would be the
    // "Map field" mislabelling bug reborn by region. Short label = the source
    // up to its first parenthetical; the full string rides in the hover title.
    const c = mesh.cycle;
    const m = /^(\d{4})(\d{2})(\d{2})(\d{2})$/.exec(c);
    const shortSrc = (mesh.source.split(" (")[0] || "Forecast field").trim();
    srcline.textContent =
      m !== null
        ? `${shortSrc} · run ${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[4]}z`
        : shortSrc;
    srcline.title = mesh.source;
  };
  // The step currently painted, so a cursor move that lands on the same field step
  // does not repaint. This matters now that the transport can drive the playhead at
  // ~10 hours a second: every showFieldHour swaps the image source, and swapping it
  // again before the last load settled aborts that load — MapLibre reports each one
  // as an AbortError. Past f120 the field is 3-hourly, so a third of the playhead's
  // steps land on the step already on screen and the repaint is pure waste anyway.
  let shownHour: number | null = null;
  const setHour = (i: number, force = false): void => {
    if (map === null || mesh === null) return;
    hour = Math.max(0, Math.min(mesh.timesUtc.length - 1, i));
    if (!force && hour === shownHour) return;
    shownHour = hour;
    showFieldHour(map, hour);
    syncArrows(); // the arrows follow the same displayed step as the raster
    setReadout();
  };

  // Scrub coalescing.
  //
  // A drag along the ruler crosses a new forecast hour on very nearly every
  // pointermove, and each NEW hour costs a full raster: renderHour paints the
  // grid, toDataURL serialises it, and MapLibre decodes that back into a GPU
  // texture and redraws the map. Thirty of those inside one short drag is what
  // pinned the main thread — a CPU profile of a scrub puts the time in toDataURL,
  // putImageData, texImage2D and MapLibre's own draw, not in the charts (which
  // are rAF-coalesced canvases and were never the problem, however much they
  // looked like it). The per-hour cache only helps the SECOND pass over an hour;
  // the first sweep across a week is all cache misses.
  //
  // So the field repaints at its own rate rather than the pointer's: at most one
  // per FIELD_MIN_MS, and always a trailing one, so the hour the pointer finally
  // lands on is the hour left on screen. Correctness is unchanged — only the
  // intermediate frames nobody can perceive are dropped.
  const FIELD_MIN_MS = 120;
  let pendingHour: number | null = null;
  let lastPaintMs = 0;
  let trailing: number | null = null;

  const flushHour = (): void => {
    trailing = null;
    if (pendingHour === null) return;
    const i = pendingHour;
    pendingHour = null;
    lastPaintMs = performance.now();
    setHour(i);
  };

  const requestHour = (i: number): void => {
    pendingHour = i;
    const wait = FIELD_MIN_MS - (performance.now() - lastPaintMs);
    if (wait <= 0) {
      if (trailing !== null) {
        window.clearTimeout(trailing);
        trailing = null;
      }
      flushHour();
      return;
    }
    // Already scheduled: the newer hour simply replaces the pending one.
    if (trailing === null) trailing = window.setTimeout(flushHour, wait);
  };

  // The field follows the playhead, and never pushes back. It snaps an instant to
  // its nearest step, and past f120 those steps are 3-hourly — echoing the snapped
  // time would drag the hourly charts onto the field's coarser grid, so the two
  // stay honest about running on different sampling.
  const unsubscribeCursor = subscribeCursor((t) => {
    if (t === null || mesh === null || map === null) return;
    requestHour(indexAtInstant(mesh, t));
    // setHour repaints only when the STEP changed, and running off the end holds
    // the step still — so the note would never appear or clear without this.
    setReadout();
  });

  const teardown = (msg: string): void => {
    if (map !== null) {
      clearField(map);
      clearArrows(map); // the arrows annotate the field — off means both off
    }
    mesh = null;
    lArrows.style.display = "none";
    pick.style.display = "none";
    arrowline.textContent = "";
    shownHour = null; // nothing painted any more, so nothing to skip against
    lastKind = null;
    lastWind = null;
    lastWave = null;
    bar.style.display = "none";
    legend.style.display = "none";
    layerBig.textContent = "";
    srcline.textContent = "";
    status.textContent = msg;
    syncBox();
  };

  const showActive = (): void => {
    if (map === null || mesh === null || kind === null) return;
    const lim = limits();
    setField(map, mesh, kind, lim, hour);
    syncArrows(); // after setField, so the symbol layer lands above the raster
    shownHour = hour; // setField paints directly, so record what is on screen
    lastKind = kind;
    lastWind = lim.windKn;
    lastWave = lim.waveM;
    bar.style.display = "";
    legend.style.display = "";
    // Clear whatever teardown() last said. `status` is written in exactly one
    // place — teardown — and the mesh always arrives AFTER first paint, so the
    // startup gap reliably set "Field loading — check Systems if this persists."
    // and nothing ever took it back down: production served a fully-painted
    // field under a note telling the operator to go check a healthy Systems
    // board. A message about a state we are no longer in is worse than silence.
    status.textContent = "";
    setReadout();
    setLegend();
    syncBox();
  };

  const apply = (): void => {
    if (kind === null) {
      teardown("");
      return;
    }
    if (map === null) return;
    if (ctx.mesh === null) {
      // A null mesh no longer means "outside the coverage box" — the grid is
      // global, so there is no outside. It means the field has not ARRIVED:
      // still loading, or the fetch is failing (in which case Systems says so).
      teardown("Field loading — check Systems if this persists.");
      return;
    }
    const fresh = ctx.mesh !== mesh;
    if (fresh) {
      mesh = ctx.mesh;
      emptySteps = []; // gap verdicts belong to the old grid's window
      meshHasData = undefined;
      shownHour = null; // a different grid: whatever was painted is not on it
      // Land on the hour already under the playhead, so a new basin does not yank
      // you back to "now" in the middle of an investigation.
      const held = getCursor();
      hour = held !== null ? indexAtInstant(mesh, held) : nowIndex(mesh);
    }
    const lim = limits();
    if (fresh || kind !== lastKind) {
      showActive(); // new grid, or switch layer — the mesh holds both variables
    } else if (lim.windKn !== lastWind || lim.waveM !== lastWave) {
      // Recolour on a change to EITHER limit: the Limits layer reads both, so a
      // wave-limit edit alone must invalidate the surface or it keeps stale colour.
      // For a single-variable layer the untouched limit's render is identical, so
      // recolouring on it is a rare, harmless repaint rather than a wrong one.
      lastWind = lim.windKn;
      lastWave = lim.waveM;
      recolorField(map, lim);
      setLegend();
    }
  };

  // Three exclusive layers. Ticking one clears the other two; unticking the active
  // one leaves none (kind null → basemap). One path so the three cannot disagree.
  const selectLayer = (next: FieldLayer | null): void => {
    cbLimits.checked = next === "limits";
    cbWind.checked = next === "wind";
    cbWave.checked = next === "wave";
    kind = next;
    apply();
    opts.onLayerChange?.(next);
  };
  cbLimits.addEventListener("change", () => selectLayer(cbLimits.checked ? "limits" : null));
  cbWind.addEventListener("change", () => selectLayer(cbWind.checked ? "wind" : null));
  cbWave.addEventListener("change", () => selectLayer(cbWave.checked ? "wave" : null));

  return {
    toggles,
    kind: () => kind,
    update(map_: MlMap | null, ctx_: WindCtx): void {
      map = map_;
      ctx = ctx_;
      apply();
    },
    destroy(): void {
      unsubscribeCursor();
      // A trailing repaint must not fire into a torn-down map. (setHour also
      // guards on a null mesh, so this is belt and braces, not the only defence.)
      if (trailing !== null) {
        window.clearTimeout(trailing);
        trailing = null;
      }
    },
  };
}
